import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Info, RotateCcw, Trash2 } from "lucide-react";
import {
  errorMessage,
  ApiError,
  EXCLUDABLE_KINDS,
  ACTIVITY_KIND_LABELS,
  ACTIVITY_KIND_NOUN,
  IMPORT_PREFERENCES_COPY,
  kindSourceSummary,
} from "@atlas/shared";
import {
  bulkDeleteActivities,
  fetchActivities,
  fetchImportPreferences,
  fetchRemovedActivities,
  restoreRemovedActivities,
  updateImportPreferences,
} from "../api/activities";
import type {
  ActivityKind,
  ImportPreferencesResponse,
  RemovedActivityGroup,
} from "../api/activities";
import { AtlasPanel } from "../components/AtlasPanel";
import { Button } from "../components/Button";
import { Skel } from "../components/Skeleton";
import { BulkRemoveDialog } from "./BulkRemoveDialog";
import { invalidateAfterActivityChange } from "../lib/activity-queries";

// ─── "What Atlas imports" (spec 26 / ATL-112) ─────────────────────────────────
// One preference across every automatic source. The copy carries the weight:
// each toggle names the provider types it covers for the sources the user has
// connected, switching Walks off explains the hike-vs-walk trap, and anything
// Atlas removes or skips is summarised below with a one-click way back.

type ExcludableKind = "hike" | "walk" | "run";

const SOURCE_LABELS: Record<string, string> = {
  strava: "Strava",
  komoot: "Komoot",
  apple_health: "Apple Health",
  health_connect: "Health Connect",
  garmin: "Garmin",
  gpx: "GPX",
};

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="note"
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        fontSize: 12.5,
        lineHeight: 1.5,
        color: "var(--text-secondary)",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-md)",
        padding: "8px 10px",
        minWidth: 0,
      }}
    >
      <Info size={14} style={{ flexShrink: 0, marginTop: 2, color: "var(--text-muted)" }} />
      <span>{children}</span>
    </div>
  );
}

function nounFor(kind: ActivityKind, n: number): string {
  const noun = ACTIVITY_KIND_NOUN[kind];
  return n === 1 ? noun.one : noun.many;
}

function KindToggleRow({
  kind,
  on,
  disabled,
  connected,
  existingCount,
  skippedCount,
  onChange,
  onRemove,
  onBringBack,
  bringBackPending,
}: {
  kind: ExcludableKind;
  on: boolean;
  disabled: boolean;
  connected: string[];
  existingCount: number;
  skippedCount: number;
  onChange: (on: boolean) => void;
  onRemove: () => void;
  onBringBack: () => void;
  bringBackPending: boolean;
}) {
  const summary = kindSourceSummary(kind, connected);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "10px 0",
        borderTop: "1px solid var(--border-subtle)",
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, minWidth: 0 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
            {ACTIVITY_KIND_LABELS[kind]}
          </div>
          {summary && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, lineHeight: 1.45 }}>
              {summary}
            </div>
          )}
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: disabled ? "default" : "pointer", flexShrink: 0 }}>
          <input
            type="checkbox"
            role="switch"
            aria-checked={on}
            aria-label={`Import ${ACTIVITY_KIND_NOUN[kind].many}`}
            checked={on}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: "var(--accent)" }}
          />
          <span style={{ fontSize: 12, color: "var(--text-muted)", width: 22 }}>{on ? "On" : "Off"}</span>
        </label>
      </div>

      {!on && kind === "walk" && <Callout>{IMPORT_PREFERENCES_COPY.walksOff}</Callout>}

      {!on && existingCount > 0 && (
        <div className="r-wrap" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Button variant="ghost" size="sm" leftIcon={<Trash2 size={13} />} onClick={onRemove}>
            Remove your {existingCount.toLocaleString("en-GB")} {nounFor(kind, existingCount)}
          </Button>
          <Link
            to="/activities"
            search={{ kind, select: 1 }}
            style={{ fontSize: 12, color: "var(--text-muted)", textDecoration: "none" }}
          >
            or pick them one by one
          </Link>
        </div>
      )}

      {on && skippedCount > 0 && (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RotateCcw size={13} />}
          disabled={bringBackPending}
          onClick={onBringBack}
          style={{ alignSelf: "flex-start" }}
        >
          {bringBackPending
            ? "Bringing back…"
            : `Bring back the ${skippedCount.toLocaleString("en-GB")} ${nounFor(kind, skippedCount)} Atlas skipped`}
        </Button>
      )}
    </div>
  );
}

// ─── Removed activities summary ──────────────────────────────────────────────
// One line per source × kind × reason, never a list: however many activities
// someone removes, the card stays a few lines tall. The rows live on the
// Activities page under ?removed=1.

function relativeDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return "today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "yesterday";
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
}

function groupKey(g: RemovedActivityGroup): string {
  return `${g.sourceType}:${g.kind}:${g.reason}`;
}

function groupLabel(g: RemovedActivityGroup): string {
  const n = g.count.toLocaleString("en-GB");
  const noun = g.kind === "other" ? (g.count === 1 ? "activity" : "activities") : nounFor(g.kind, g.count);
  const source = SOURCE_LABELS[g.sourceType] ?? g.sourceType;
  const when = relativeDay(g.latestAt);
  const how = g.reason === "removed" ? "removed" : "skipped by your settings";
  return `${n} ${noun} from ${source} · ${how}${when ? `, ${when}` : ""}`;
}

function RemovedSummary({ onDeviceSync }: { onDeviceSync: () => void }) {
  const queryClient = useQueryClient();
  const [restoring, setRestoring] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<{ key: string; text: string } | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const query = useQuery({
    queryKey: ["activities/removed", { summary: true }],
    queryFn: () => fetchRemovedActivities({ limit: 1, offset: 0 }),
    staleTime: 30_000,
  });

  const restoreMutation = useMutation({
    mutationFn: (g: RemovedActivityGroup) =>
      restoreRemovedActivities({
        filter: { reason: g.reason, kind: g.kind, source: g.sourceType },
        expectedCount: g.count,
      }),
    onMutate: (g) => {
      setRestoring(groupKey(g));
      setError(null);
    },
    onSuccess: (res, g) => {
      invalidateAfterActivityChange(queryClient);
      if (res.needsDeviceSync > 0) {
        onDeviceSync();
        setNotice({ key: groupKey(g), text: IMPORT_PREFERENCES_COPY.deviceSync });
      } else {
        setNotice({ key: groupKey(g), text: "Back on your record; re-matching now." });
      }
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ["activities/removed"] });
        setError("That group changed, so nothing was restored. Check the count and try again.");
        return;
      }
      setError(errorMessage(err, "Couldn't bring those back. Try again."));
    },
    onSettled: () => setRestoring(null),
  });

  const groups = query.data?.groups ?? [];
  const total = groups.reduce((a, g) => a + g.count, 0);

  return (
    <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 10, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Removed activities</span>
        {query.data && (
          <span className="tnum" style={{ fontSize: 11, color: "var(--text-muted)" }}>
            {total.toLocaleString("en-GB")}
          </span>
        )}
      </div>

      {query.isLoading ? (
        <Skel height="32px" />
      ) : query.isError ? (
        <p style={{ fontSize: 12, color: "var(--status-danger)", margin: 0 }}>Couldn't load removed activities.</p>
      ) : groups.length === 0 ? (
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: 0, lineHeight: 1.5 }}>
          Nothing removed or skipped. Anything you remove, or Atlas skips because of the settings above, is summarised here.
        </p>
      ) : (
        <>
          {groups.map((g) => {
            const key = groupKey(g);
            return (
              <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                <div className="r-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
                  <span style={{ fontSize: 13, color: "var(--text-primary)", minWidth: 0 }}>{groupLabel(g)}</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<RotateCcw size={13} />}
                    disabled={restoring !== null}
                    onClick={() => restoreMutation.mutate(g)}
                  >
                    {restoring === key ? "Restoring…" : "Restore all"}
                  </Button>
                </div>
                {notice?.key === key && (
                  <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>{notice.text}</p>
                )}
              </div>
            );
          })}
          {error && <p style={{ fontSize: 12, color: "var(--status-danger)", margin: 0 }}>{error}</p>}
          <Link
            to="/activities"
            search={{ removed: 1 }}
            style={{ fontSize: 12.5, color: "var(--text-accent)", textDecoration: "none", alignSelf: "flex-start" }}
          >
            See removed activities
          </Link>
        </>
      )}
    </div>
  );
}

// ─── Panel ───────────────────────────────────────────────────────────────────

export function ImportPreferencesPanel({ connectedProviders }: { connectedProviders: string[] }) {
  const queryClient = useQueryClient();
  const [deviceSyncNote, setDeviceSyncNote] = React.useState(false);
  const [bringingBack, setBringingBack] = React.useState<ExcludableKind | null>(null);
  const [removeKind, setRemoveKind] = React.useState<ExcludableKind | null>(null);
  const [removeError, setRemoveError] = React.useState<string | null>(null);

  const prefsQuery = useQuery({
    queryKey: ["activities/import-preferences"],
    queryFn: fetchImportPreferences,
  });
  // Unfiltered per-kind counts ride on the activities list; a single small page
  // is enough to read them.
  const countsQuery = useQuery({
    queryKey: ["activities", { source: null, kind: null, page: 1, forCounts: true }],
    queryFn: () => fetchActivities(1, 0),
    staleTime: 30_000,
  });
  const kindCounts = new Map<ActivityKind, number>();
  for (const k of countsQuery.data?.kinds ?? []) kindCounts.set(k.kind, k.count);

  const toggleMutation = useMutation({
    mutationFn: (excludedKinds: ActivityKind[]) => updateImportPreferences(excludedKinds),
    onMutate: async (excludedKinds) => {
      await queryClient.cancelQueries({ queryKey: ["activities/import-preferences"] });
      const prev = queryClient.getQueryData<ImportPreferencesResponse>(["activities/import-preferences"]);
      if (prev) queryClient.setQueryData(["activities/import-preferences"], { ...prev, excludedKinds });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["activities/import-preferences"], ctx.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["activities/import-preferences"] });
    },
  });

  const bringBackMutation = useMutation({
    mutationFn: (kind: ExcludableKind) =>
      restoreRemovedActivities({
        filter: { reason: "skipped_by_preference", kind },
        expectedCount: prefsQuery.data?.skippedCounts[kind] ?? 0,
      }),
    onMutate: (kind) => setBringingBack(kind),
    onSuccess: (res) => {
      invalidateAfterActivityChange(queryClient);
      if (res.needsDeviceSync > 0) setDeviceSyncNote(true);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ["activities/import-preferences"] });
      }
    },
    onSettled: () => setBringingBack(null),
  });

  // In-place "Remove your N runs": the filter form of bulk delete with the
  // count the user was shown, so a sync landing mid-dialog cannot over-remove.
  const removeMutation = useMutation({
    mutationFn: (kind: ExcludableKind) =>
      bulkDeleteActivities({ filter: { kind }, expectedCount: kindCounts.get(kind) ?? 0 }),
    onSuccess: () => {
      invalidateAfterActivityChange(queryClient);
      setRemoveKind(null);
      setRemoveError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ["activities"] });
        setRemoveError("The count changed while you were deciding, so nothing was removed. Check it and try again.");
        return;
      }
      setRemoveError(errorMessage(err, "Couldn't remove those. Try again."));
    },
  });

  const excluded = new Set<ActivityKind>(prefsQuery.data?.excludedKinds ?? []);

  function setKind(kind: ExcludableKind, on: boolean) {
    const next = new Set(excluded);
    if (on) next.delete(kind);
    else next.add(kind);
    toggleMutation.mutate([...next]);
  }

  return (
    <AtlasPanel title={IMPORT_PREFERENCES_COPY.title}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
        <p style={{ fontSize: 13, lineHeight: 1.55, color: "var(--text-secondary)", margin: 0 }}>
          {IMPORT_PREFERENCES_COPY.intro}
        </p>

        {prefsQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[0, 1, 2].map((i) => <Skel key={i} height="48px" />)}
          </div>
        ) : prefsQuery.isError ? (
          <p style={{ fontSize: 12, color: "var(--status-danger)", margin: 0 }}>
            Couldn't load your import settings. Everything imports until they load.
          </p>
        ) : (
          <div>
            {(EXCLUDABLE_KINDS as readonly ExcludableKind[]).map((kind) => (
              <KindToggleRow
                key={kind}
                kind={kind}
                on={!excluded.has(kind)}
                disabled={toggleMutation.isPending}
                connected={connectedProviders}
                existingCount={kindCounts.get(kind) ?? 0}
                skippedCount={prefsQuery.data?.skippedCounts[kind] ?? 0}
                onChange={(on) => setKind(kind, on)}
                onRemove={() => { setRemoveError(null); setRemoveKind(kind); }}
                onBringBack={() => bringBackMutation.mutate(kind)}
                bringBackPending={bringingBack === kind}
              />
            ))}
          </div>
        )}

        {toggleMutation.isError && (
          <p style={{ fontSize: 12, color: "var(--status-danger)", margin: 0 }}>
            {errorMessage(toggleMutation.error, "Couldn't save that. Try again.")}
          </p>
        )}
        {bringBackMutation.isError && (
          <p style={{ fontSize: 12, color: "var(--status-danger)", margin: 0 }}>
            {bringBackMutation.error instanceof ApiError && bringBackMutation.error.status === 409
              ? "The skipped list changed, so nothing was restored. Try again."
              : errorMessage(bringBackMutation.error, "Couldn't bring those back. Try again.")}
          </p>
        )}
        {deviceSyncNote && <Callout>{IMPORT_PREFERENCES_COPY.deviceSync}</Callout>}

        <p style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text-muted)", margin: 0 }}>
          {IMPORT_PREFERENCES_COPY.notMatched}
        </p>
        <p style={{ fontSize: 12, lineHeight: 1.5, color: "var(--text-muted)", margin: 0 }}>
          {IMPORT_PREFERENCES_COPY.allowlist}
        </p>

        <RemovedSummary onDeviceSync={() => setDeviceSyncNote(true)} />
      </div>

      {removeKind && (
        <BulkRemoveDialog
          count={kindCounts.get(removeKind) ?? 0}
          kind={removeKind}
          pending={removeMutation.isPending}
          error={removeError}
          onConfirm={() => removeMutation.mutate(removeKind)}
          onClose={() => { if (!removeMutation.isPending) { setRemoveKind(null); setRemoveError(null); } }}
        />
      )}
    </AtlasPanel>
  );
}
